import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import {
  ArrowLeftIcon,
  EditIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  DollarSignIcon,
  Loader2Icon,
  FileTextIcon,
  PlayIcon,
  CheckCircleIcon,
  NavigationIcon,
  PhoneIcon,
  CameraIcon,
  StickyNoteIcon,
  MessageSquareIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BuildingIcon,
} from 'lucide-react'
import { useAppShellMode } from '../hooks/useAppShellMode'
import { MOBILE_BOTTOM_NAV_HEIGHT } from '../components/MobileBottomNav'
import {
  mobile,
  MobileStatusBadge,
  MobileQuickAction,
  MobilePageHeader,
} from '../components/mobile'
import { supabase } from '../lib/supabase'
import { ConversationsPanel } from '../components/ConversationsPanel'
import { ViewTabBar, ViewSection, ViewStatCard, ViewEmptyState } from '../components/ViewPageLayout'
import { getEntityActivities, ActivityLog, logActivity, ActivityTypes, formatActivityMeta } from '../utils/activityLogger'
import { getServiceTypeLabel } from '../lib/serviceTypeOptions'
import { fetchContractMilestones, summarizeMilestones } from '../lib/milestoneBilling'
import {
  getAllowedStatusTransitions,
  getStatusColor,
  formatStatusLabel,
  resolvePostCompletionStatus,
  filterStatusTransitionsForPermissions,
  isReviewWorkflowStatus,
} from '../lib/jobStatusWorkflow'
import {
  fetchJobAssignment,
  fetchJobInvoiceLinks,
  fetchJobSiblings,
  fetchJobTimeEntries,
  type JobAssignmentSummary,
  type JobInvoiceLink,
  type JobSiblingSummary,
  type JobTimeEntrySummary,
} from '../api/jobRelatedRecords'
import { formatUnitTypeLabel } from '../lib/unitTypeHelpers'
import { JobAssignmentFields } from '../components/job/JobAssignmentFields'
import { JobTaskChecklist } from '../components/job/JobTaskChecklist'
import { ApartmentJobWork } from '../components/job/ApartmentJobWork'
import { JobCommentsThread } from '../components/job/JobCommentsThread'
import { JobChatPanel } from '../components/job/JobChatPanel'
import { PatchInPanel } from '../components/job/PatchInPanel'
import { JobGallery } from '../components/job/JobGallery'
import { JobFieldCapturePanel } from '../components/job/JobFieldCapturePanel'
import { JobCompletionModal } from '../components/job/JobCompletionModal'
import { CompletionExceptionModal } from '../components/job/CompletionExceptionModal'
import { JobQrRequirementsPicker } from '../components/job/JobQrRequirementsPicker'
import {
  canCompleteJobWithException,
  completionExceptionUpdate,
} from '../lib/jobCompletionException'
import { useAppSettings } from '../hooks/useAppSettings'
import { evaluateJobCompletionGate, isJobChecklistComplete } from '../lib/jobCompletionGate'
import { fetchJobTasks } from '../api/jobWorkflow'
import {
  fetchAssignmentHistory,
  formatAssigneeTeamLabel,
  type AssignmentHistoryRow,
} from '../lib/jobAssignmentHistory'
import { usePermissions } from '../hooks/usePermissions'
import { propertyDetailPath, usesFieldHome, usesOpsDashboard } from '../lib/fieldHome'
import { fetchFieldJobLineItems } from '../api/fieldJobAccess'
import {
  jobCompleteGpsPatch,
  jobStartGpsPatch,
} from '../lib/geolocation'
import {
  captureOptionalJobSiteGps,
  gateJobSiteAction,
  isFieldGpsError,
} from '../lib/fieldGpsGate'
import {
  assertCanStartOrResumeJob,
  closeStaleJobTimeEntry,
  clockOut,
  fetchEmployeeClockState,
  resolveEmployeeIdForUser,
} from '../lib/timeClockActions'
import { stampJobHoursActualFromTimeEntries } from '../lib/jobActualHours'

interface Job {
  id: string
  job_number: string
  title: string
  description?: string
  service_type?: string
  scheduled_date?: string
  scheduled_start_time?: string
  scheduled_end_time?: string
  estimated_duration?: number
  status: string
  priority: 'low' | 'medium' | 'high'
  quoted_amount?: number
  actual_amount?: number
  billing_type?: string
  billing_contract_id?: string
  total_billable_amount?: number
  hours_required?: number
  hours_actual?: number
  materials_cost?: number
  visit_group_id?: string
  parent_recurring_job_id?: string
  carried_from_job_id?: string
  actual_start_time?: string
  actual_end_time?: string
  start_latitude?: number | null
  start_longitude?: number | null
  start_accuracy_m?: number | null
  complete_latitude?: number | null
  complete_longitude?: number | null
  complete_accuracy_m?: number | null
  assigned_crew_id?: string
  assigned_employee_id?: string
  tags?: string[]
  customer_id: string
  property_id: string
  proposal_id?: string
  contract_id?: string
  source_city_record_id?: string
  is_recurring?: boolean
  recurrence_type?: string
  recurrence_days?: number[]
  dsny_integration?: boolean
  dsny_pickup_days?: string[]
  notes?: string
  created_at: string
  updated_at: string
  // Joined data
  customer?: {
    id: string
    company_name?: string
    contact_first_name: string
    contact_last_name: string
    email?: string
    phone?: string
  }
  property?: {
    id?: string
    name: string
    address: string
    city?: string
    state?: string
    zip_code?: string
  }
  proposal?: {
    title: string
    proposal_number: string
  }
  contract?: {
    contract_number: string
    title: string
  }
}

interface JobLineItem {
  id: string
  description: string
  quantity: number
  unit_type?: string
  unit_price: number
  total_price: number
  is_variable?: boolean
  billing_status?: string
  contract_service_id?: string | null
}

function formatStatusUpdateError(error: unknown): string {
  if (!error) return ''
  if (typeof error === 'string') return error
  if (typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string }
    return [e.message, e.details, e.hint].filter(Boolean).join(' — ')
  }
  return String(error)
}

export function JobDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const fromState =
    location.state && typeof location.state === 'object' && 'from' in location.state
      ? String((location.state as { from?: unknown }).from || '')
      : ''
  const openCompletionFromChecklist =
    location.state &&
    typeof location.state === 'object' &&
    (location.state as { openCompletion?: unknown }).openCompletion === true

  const goBack = () => {
    if (returnTo) {
      navigate(decodeURIComponent(returnTo))
      return
    }
    if (fromState) {
      navigate(fromState)
      return
    }
    if (window.history.length > 1) {
      navigate(-1)
      return
    }
    navigate('/jobs')
  }
  const [job, setJob] = useState<Job | null>(null)
  const [loading, setLoading] = useState(true)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [activities, setActivities] = useState<ActivityLog[]>([])
  const [activeTab, setActiveTab] = useState<
    'overview' | 'work' | 'line_items' | 'gallery' | 'notes' | 'chat' | 'activity'
  >('overview')
  const tabsPanelRef = useRef<HTMLDivElement>(null)
  const consumedOpenCompletionRef = useRef(false)
  const [galleryAutoCaptureToken, setGalleryAutoCaptureToken] = useState(0)
  const [linkedCityRecord, setLinkedCityRecord] = useState<any | null>(null)
  const [progressBillingNote, setProgressBillingNote] = useState<string | null>(null)
  const [jobLineItems, setJobLineItems] = useState<JobLineItem[]>([])
  const [timeEntries, setTimeEntries] = useState<JobTimeEntrySummary[]>([])
  const [invoiceLinks, setInvoiceLinks] = useState<JobInvoiceLink[]>([])
  const [siblingJobs, setSiblingJobs] = useState<JobSiblingSummary[]>([])
  const [assignment, setAssignment] = useState<JobAssignmentSummary | null>(null)
  const [assignmentHistory, setAssignmentHistory] = useState<AssignmentHistoryRow[]>([])
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [pendingStatus, setPendingStatus] = useState<string | null>(null)
  const [showExceptionModal, setShowExceptionModal] = useState(false)
  const [exceptionGateMessage, setExceptionGateMessage] = useState<string | null>(null)
  const [checklistComplete, setChecklistComplete] = useState(false)
  const [mobileSections, setMobileSections] = useState({
    customer: true,
    property: true,
    assigned: true,
  })
  const { photoPolicy } = useAppSettings()
  const { can, canSeePricing, profile, isSuperAdmin } = usePermissions()
  const canExceptionComplete = canCompleteJobWithException(profile?.role)
  const canEditJob = can('jobs.edit')
  const canCancelJob = can('jobs.cancel')
  const canApproveReview = can('review.approve')
  const canAssignJob = can('jobs.dispatch') || can('jobs.edit')
  const canJobChat = can('communication.chat')
  const isMobileShell = useAppShellMode() === 'mobile'
  const fieldHome = usesFieldHome(profile?.role, isSuperAdmin)

  useEffect(() => {
    if (id) {
      fetchJob(id)
    }
  }, [id, canSeePricing])

  useEffect(() => {
    if (!id || !isMobileShell || job?.status !== 'in_progress') {
      setChecklistComplete(false)
      return
    }
    let cancelled = false
    void fetchJobTasks(id)
      .then((tasks) => {
        if (!cancelled) setChecklistComplete(isJobChecklistComplete(tasks))
      })
      .catch(() => {
        if (!cancelled) setChecklistComplete(false)
      })
    return () => {
      cancelled = true
    }
  }, [id, isMobileShell, job?.status])

  const fetchJob = async (jobId: string) => {
    try {
      setLoading(true)

      const { data: jobData, error: jobError } = await supabase
        .from('jobs')
        .select(
          `
          *,
          customer:customers(id, company_name, contact_first_name, contact_last_name, email, phone),
          property:properties(id, name, address, city, state, zip_code),
          proposal:proposals(id, title, proposal_number),
          contract:contracts!jobs_contract_id_fkey(id, contract_number, title),
          billing_contract:contracts!jobs_billing_contract_id_fkey(
            id, contract_number, title, billing_frequency, contract_total_amount, total_amount
          )
        `
        )
        .eq('id', jobId)
        .single()

      if (jobError) throw jobError
      if (!jobData) {
        setJob(null)
        return
      }

      setJob(jobData)

      const { data: lineItemRows } = canSeePricing
        ? await supabase
            .from('job_line_items')
            .select(
              'id, description, quantity, unit_type, unit_price, total_price, is_variable, billing_status, contract_service_id'
            )
            .eq('job_id', jobId)
            .order('sort_order', { ascending: true })
        : { data: null as JobLineItem[] | null }

      if (canSeePricing) {
        setJobLineItems((lineItemRows as JobLineItem[]) || [])
      } else {
        const fieldLines = await fetchFieldJobLineItems(jobId)
        setJobLineItems(
          fieldLines.map((l) => ({
            id: l.id,
            description: l.description,
            quantity: Number(l.quantity ?? l.actual_quantity ?? 0),
            unit_type: l.unit_type ?? undefined,
            unit_price: 0,
            total_price: 0,
            is_variable: l.is_variable ?? undefined,
            billing_status: undefined,
          }))
        )
      }

      try {
        const [entries, invoices, siblings, assign, history] = await Promise.all([
          fetchJobTimeEntries(jobId),
          canSeePricing ? fetchJobInvoiceLinks(jobId) : Promise.resolve([] as JobInvoiceLink[]),
          fetchJobSiblings(jobData),
          fetchJobAssignment(jobData),
          fetchAssignmentHistory(jobId).catch(() => [] as AssignmentHistoryRow[]),
        ])
        setTimeEntries(entries)
        setInvoiceLinks(invoices)
        setSiblingJobs(siblings)
        setAssignment(assign)
        setAssignmentHistory(history)
      } catch {
        setTimeEntries([])
        setInvoiceLinks([])
        setSiblingJobs([])
        setAssignment(null)
        setAssignmentHistory([])
      }

      const billingContract = (jobData as any).billing_contract
      if (
        canSeePricing &&
        jobData?.billing_type === 'contract' &&
        billingContract?.billing_frequency === 'progress'
      ) {
        try {
          const milestones = await fetchContractMilestones(billingContract.id)
          const total =
            Number(billingContract.contract_total_amount) ||
            Number(billingContract.total_amount) ||
            0
          const summary = summarizeMilestones(milestones, total)
          const next = summary.nextPending
            ? `Next milestone: ${summary.nextPending.label} ($${Number(summary.nextPending.amount).toFixed(2)})`
            : 'No pending milestones'
          setProgressBillingNote(
            `Progress billing — billed $${summary.billed.toFixed(2)} of $${summary.total.toFixed(2)} (remaining $${summary.remaining.toFixed(2)}). ${next}.`
          )
        } catch {
          setProgressBillingNote(null)
        }
      } else {
        setProgressBillingNote(null)
      }

      // Fetch linked city record (if job was created from a violation/complaint)
      if (jobData?.source_city_record_id) {
        const { data: crData } = await supabase
          .from('property_city_records')
          .select('id, source, source_uid, title, description, category, record_date, amount_due')
          .eq('id', jobData.source_city_record_id)
          .single()
        setLinkedCityRecord(crData || null)
      } else {
        setLinkedCityRecord(null)
      }

      // Fetch activities
      const activityData = await getEntityActivities('job', jobId)
      setActivities(activityData)
    } catch (error) {
      console.error('Error fetching job:', error)
      alert('Error loading job details')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (newStatus: string) => {
    if (!job || !id) return
    if (!newStatus?.trim()) return

    if (newStatus === 'completed' && job.status !== 'completed') {
      const gate = await evaluateJobCompletionGate(id, photoPolicy)
      if (!gate.ok) {
        if (canExceptionComplete) {
          setExceptionGateMessage(gate.message || 'Checklist or photos incomplete')
          setPendingStatus(newStatus)
          setShowExceptionModal(true)
          if (gate.redirectTo) setActiveTab(gate.redirectTo)
          return
        }
        alert(gate.message)
        if (gate.redirectTo) setActiveTab(gate.redirectTo)
        return
      }
      setPendingStatus(newStatus)
      setShowCompletionModal(true)
      return
    }

    await applyStatusChange(newStatus)
  }

  useEffect(() => {
    if (!openCompletionFromChecklist || loading || !job || !id) return
    if (job.status === 'completed') return
    if (consumedOpenCompletionRef.current) return
    consumedOpenCompletionRef.current = true
    // Consume one-shot flag from checklist "Complete job" so refresh does not re-open.
    navigate(location.pathname, {
      replace: true,
      state: fromState ? { from: fromState } : null,
    })
    void handleStatusChange('completed')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot from navigation state
  }, [openCompletionFromChecklist, loading, job?.id, job?.status, id])

  const applyStatusChange = async (
    newStatus: string,
    exception?: { reason: string }
  ) => {
    if (!job || !id) return
    if (!newStatus?.trim()) return

    try {
      setUpdatingStatus(true)
      const persistedStatus = resolvePostCompletionStatus(newStatus)
      if (!persistedStatus) return
      if (
        !canApproveReview &&
        newStatus !== 'completed' &&
        isReviewWorkflowStatus(persistedStatus)
      ) {
        alert('You do not have permission to perform this review action.')
        return
      }
      const updateData: Record<string, unknown> = { status: persistedStatus }
      const opsOverride = usesOpsDashboard(profile?.role, isSuperAdmin)
      let gated: Awaited<ReturnType<typeof gateJobSiteAction>> | null = null
      if (newStatus === 'in_progress' || newStatus === 'completed') {
        if (opsOverride) {
          gated = await captureOptionalJobSiteGps(id)
        } else {
          gated = await gateJobSiteAction({
            jobId: id,
            actionLabel: newStatus === 'in_progress' ? 'Job start' : 'Job complete',
            attemptType: newStatus === 'in_progress' ? 'job_start' : 'job_complete',
          })
        }
      }

      if (newStatus === 'in_progress') {
        if (!opsOverride) {
          const { data: auth } = await supabase.auth.getUser()
          if (auth.user?.id) {
            const empId = await resolveEmployeeIdForUser(auth.user.id)
            if (empId) await assertCanStartOrResumeJob(empId, id)
          }
        }
        updateData.actual_start_time = new Date().toISOString()
        Object.assign(updateData, jobStartGpsPatch(gated?.position ?? null))
      } else if (newStatus === 'completed') {
        updateData.actual_end_time = new Date().toISOString()
        Object.assign(updateData, jobCompleteGpsPatch(gated?.position ?? null))
        if (exception?.reason?.trim()) {
          const { data: auth } = await supabase.auth.getUser()
          Object.assign(updateData, completionExceptionUpdate(exception.reason, auth.user?.id))
        }
      }

      const { error } = await supabase.from('jobs').update(updateData).eq('id', id)

      if (error) throw error

      if (newStatus === 'completed') {
        const { data: auth } = await supabase.auth.getUser()
        const empId = auth.user?.id ? await resolveEmployeeIdForUser(auth.user.id) : null
        if (empId) {
          const clockState = await fetchEmployeeClockState(empId)
          if (
            clockState.jobEntry &&
            (clockState.jobEntry.job_id === id || !clockState.jobEntry.job_id)
          ) {
            if (gated?.geo?.position) {
              await clockOut(
                clockState.jobEntry.id,
                null,
                null,
                gated.geo,
                gated.distanceFt
              )
            } else {
              await closeStaleJobTimeEntry(clockState.jobEntry)
            }
          }
        }
        await stampJobHoursActualFromTimeEntries(id)
      }

      await logActivity({
        activity_type: newStatus === 'completed' ? ActivityTypes.JOB_COMPLETED : ActivityTypes.JOB_UPDATED,
        entity_type: 'job',
        entity_id: id,
        description: `Job status changed from "${job.status}" to "${persistedStatus}"`,
        metadata: {
          old_status: job.status,
          new_status: persistedStatus,
          ...(gated
            ? {
                latitude: gated.position.latitude,
                longitude: gated.position.longitude,
                accuracy_m: gated.position.accuracy,
                distance_ft: gated.distanceFt,
                geofence_overridden: gated.geofenceOverridden,
              }
            : {}),
        },
      })

      await fetchJob(id)
    } catch (error) {
      console.error('Error updating job status:', error)
      if (isFieldGpsError(error)) {
        if (error.code !== 'cancelled') alert(error.message)
        return
      }
      const msg = formatStatusUpdateError(error)
      alert(msg ? `Failed to update job status: ${msg}` : 'Failed to update job status')
    } finally {
      setUpdatingStatus(false)
    }
  }

  const formatTime = (time: string) => {
    if (!time) return 'N/A'
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  const formatDate = (date: string) => {
    if (!date) return 'Unscheduled'
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800'
      case 'low':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const statusActionLabel = (status: string, opts?: { mobile?: boolean }) => {
    switch (status) {
      case 'in_progress':
        return opts?.mobile ? 'Start working' : 'Start'
      case 'completed':
        return opts?.mobile ? 'Complete job' : 'Complete'
      case 'cancelled':
        return 'Cancel'
      case 'scheduled':
        return 'Schedule'
      default:
        return formatStatusLabel(status)
    }
  }

  const isPrimaryStatusAction = (status: string) =>
    status === 'in_progress' || status === 'completed'

  const runStatusAction = (nextStatus: string) => {
    if (nextStatus === 'cancelled') {
      if (!canCancelJob) return
      if (!window.confirm('Cancel this job?')) return
    }
    handleStatusChange(nextStatus)
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-gray-600">Loading job details...</div>
          </div>
        </div>
      </div>
    )
  }

  if (!job) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Job not found</h3>
            <p className="text-gray-500 mb-6">The requested job could not be found.</p>
            <button
              onClick={() => navigate('/jobs')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Jobs
            </button>
          </div>
        </div>
      </div>
    )
  }

  const allowedTransitions = filterStatusTransitionsForPermissions(
    getAllowedStatusTransitions(job.status),
    canApproveReview,
    canCancelJob
  )
  const primaryActions = allowedTransitions.filter(isPrimaryStatusAction)
  const secondaryActions = allowedTransitions.filter((s) => !isPrimaryStatusAction(s))
  const totalTimeHours = timeEntries.reduce(
    (sum, e) => sum + Number(e.total_hours || 0),
    0
  )
  const assigneeLabel = (() => {
    const active = assignmentHistory.filter((r) => !r.unassigned_at)
    const team = formatAssigneeTeamLabel(active)
    if (team) return team
    if (assignment?.crew_name || assignment?.employee_name) {
      return [assignment.crew_name, assignment.employee_name].filter(Boolean).join(' · ')
    }
    return 'Unassigned'
  })()
  const customerLabel =
    job.customer?.company_name ||
    `${job.customer?.contact_first_name || ''} ${job.customer?.contact_last_name || ''}`.trim() ||
    '—'
  const propertyAddress = [
    job.property?.address,
    [job.property?.city, job.property?.state].filter(Boolean).join(', '),
    job.property?.zip_code,
  ]
    .filter(Boolean)
    .join(', ')
  const mapsQuery = propertyAddress || job.property?.name || ''
  const mapsUrl = mapsQuery
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapsQuery)}`
    : null
  const mapsEmbedUrl = mapsQuery
    ? `https://www.google.com/maps?q=${encodeURIComponent(mapsQuery)}&output=embed`
    : null
  const customerPhone = job.customer?.phone?.replace(/[^\d+]/g, '') || ''
  const telHref = customerPhone ? `tel:${customerPhone}` : undefined
  const actionBarBottom = `calc(${MOBILE_BOTTOM_NAV_HEIGHT} + env(safe-area-inset-bottom, 0px))`
  const showMobileActionBar =
    isMobileShell && (primaryActions.length > 0 || secondaryActions.length > 0)

  const toggleMobileSection = (key: keyof typeof mobileSections) => {
    setMobileSections((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  type JobDetailTab =
    | 'overview'
    | 'work'
    | 'line_items'
    | 'gallery'
    | 'notes'
    | 'chat'
    | 'activity'

  const goToTab = (tab: JobDetailTab) => {
    setActiveTab(tab)
    window.setTimeout(() => {
      tabsPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  return (
    <div
      className="bg-gray-100 min-h-full min-w-0"
      style={
        showMobileActionBar
          ? { paddingBottom: 'calc(7.5rem + env(safe-area-inset-bottom, 0px))' }
          : undefined
      }
    >
      {/* Compact sticky header (mobile/PWA) */}
      {isMobileShell && (
        <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-gray-200/90 px-3 py-2 shadow-sm shadow-gray-900/5">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={goBack}
              className="flex items-center justify-center min-h-[44px] min-w-[44px] -ml-1 text-gray-600 active:bg-gray-100 rounded-xl flex-shrink-0"
              aria-label="Back"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div className="min-w-0 flex-1 text-center">
              <p className="text-sm font-bold text-gray-900 tabular-nums">#{job.job_number}</p>
            </div>
            {updatingStatus ? (
              <Loader2Icon className="w-5 h-5 animate-spin text-gray-400 flex-shrink-0" />
            ) : canEditJob ? (
              <button
                type="button"
                onClick={() => navigate(`/jobs/edit/${job.id}`)}
                className="flex items-center justify-center min-h-[44px] min-w-[44px] text-blue-600 active:bg-blue-50 rounded-xl flex-shrink-0"
                aria-label="Edit job"
              >
                <EditIcon className="w-5 h-5" />
              </button>
            ) : (
              <span className="w-11 flex-shrink-0" aria-hidden />
            )}
          </div>
        </div>
      )}

      <div className="p-3 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {!isMobileShell && (
          <>
          <MobilePageHeader
            sticky
            title={job.title}
            subtitle={`#${job.job_number}${job.service_type ? ` · ${getServiceTypeLabel(job.service_type)}` : ''}`}
            onBack={goBack}
            backLabel="Back"
            actions={
              <>
                {updatingStatus && <Loader2Icon className="w-5 h-5 animate-spin text-gray-400" />}
                {allowedTransitions.map((nextStatus) => (
                  <button
                    key={nextStatus}
                    type="button"
                    onClick={() => runStatusAction(nextStatus)}
                    disabled={updatingStatus}
                    className={`min-h-[40px] px-3 py-2 text-xs sm:text-sm rounded-lg border transition-colors disabled:opacity-50 ${
                      nextStatus === 'cancelled'
                        ? 'border-red-300 text-red-600 hover:bg-red-50'
                        : isPrimaryStatusAction(nextStatus)
                          ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {statusActionLabel(nextStatus)}
                  </button>
                ))}
                {canEditJob && (
                  <button
                    type="button"
                    onClick={() => navigate(`/jobs/edit/${job.id}`)}
                    className={`${mobile.primaryBtn} !min-h-[40px] !px-3 text-xs sm:text-sm`}
                  >
                    <EditIcon className="w-4 h-4" />
                    Edit
                  </button>
                )}
              </>
            }
          />
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}>
              {formatStatusLabel(job.status)}
            </span>
            <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getPriorityColor(job.priority)}`}>
              {job.priority} priority
            </span>
            {job.tags && job.tags.length > 0 && job.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700"
              >
                {tag}
              </span>
            ))}
          </div>
          </>
        )}

        {/* Mobile: Zuper-style summary + circular quick actions */}
        {isMobileShell && (
          <div className={`${mobile.surface} p-4 mb-3`}>
            <div className="flex items-start justify-between gap-2">
              <span className="text-xs font-semibold text-gray-400 tabular-nums">#{job.job_number}</span>
              <MobileStatusBadge status={job.status} />
            </div>
            <h1 className="mt-2 text-lg font-bold text-gray-900 leading-snug">{job.title}</h1>
            <p className="mt-1 text-sm text-gray-500 flex items-center gap-1.5">
              <ClockIcon className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" />
              {formatDate(job.scheduled_date || '')}
              {' · '}
              {formatTime(job.scheduled_start_time || '')} – {formatTime(job.scheduled_end_time || '')}
            </p>
            {job.service_type && (
              <p className="mt-1 text-xs text-blue-700 font-medium">
                {getServiceTypeLabel(job.service_type)}
                <span className={`ml-2 inline-flex px-2 py-0.5 rounded-full ${getPriorityColor(job.priority)}`}>
                  {job.priority} priority
                </span>
              </p>
            )}

            <div className="mt-4 flex items-start justify-around gap-1">
              <MobileQuickAction
                label="Navigate"
                icon={<NavigationIcon className="w-6 h-6" strokeWidth={2.25} />}
                href={mapsUrl || undefined}
                muted={!mapsUrl}
                disabled={!mapsUrl}
              />
              <MobileQuickAction
                label="Job Chat"
                icon={<MessageSquareIcon className="w-6 h-6" strokeWidth={2.25} />}
                onClick={() => {
                  if (!canJobChat) {
                    alert('You do not have permission to use job chat.')
                    return
                  }
                  if (!id) return
                  navigate(`/jobs/details/${id}/chat`, {
                    state: { from: `/jobs/details/${id}` },
                  })
                }}
                muted={!canJobChat}
              />
              <MobileQuickAction
                label="Add note"
                icon={<StickyNoteIcon className="w-6 h-6" strokeWidth={2.25} />}
                onClick={() => {
                  if (!id) return
                  navigate(`/jobs/details/${id}/notes`, {
                    state: { from: `/jobs/details/${id}` },
                  })
                }}
              />
              <MobileQuickAction
                label="Photo"
                icon={<CameraIcon className="w-6 h-6" strokeWidth={2.25} />}
                onClick={() => {
                  setGalleryAutoCaptureToken((n) => n + 1)
                  goToTab('gallery')
                }}
              />
              <MobileQuickAction
                label="Checklist"
                icon={<ClipboardCheckIcon className="w-6 h-6" strokeWidth={2.25} />}
                onClick={() => {
                  if (!id) return
                  navigate(`/jobs/details/${id}/checklist`, {
                    state: { from: `/jobs/details/${id}`, photoMode: !checklistComplete },
                  })
                }}
              />
            </div>
          </div>
        )}

        {canSeePricing && progressBillingNote && (
          <div className="mb-3 sm:mb-6 p-3 sm:p-4 bg-indigo-50 border border-indigo-200 rounded-2xl text-sm text-indigo-900">
            {progressBillingNote}
          </div>
        )}

        {/* Mobile map / service address card */}
        {isMobileShell && (
          <div className={`${mobile.surface} overflow-hidden mb-3`}>
            <div className="px-4 pt-3 pb-2">
              <h3 className="text-sm font-bold text-gray-900">Service Address</h3>
            </div>
            {mapsEmbedUrl && (
              <div className="relative h-36 bg-sky-50 border-y border-gray-100">
                <iframe
                  title="Job location map"
                  src={mapsEmbedUrl}
                  className="absolute inset-0 w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            )}
            <div className="p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-900">{customerLabel}</p>
                <p className="text-sm text-gray-600 mt-0.5 break-words">
                  {propertyAddress || job.property?.name || 'No address on file'}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {telHref && (
                  <a
                    href={telHref}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-blue-200 text-blue-600 active:bg-blue-50"
                    aria-label="Call customer"
                  >
                    <PhoneIcon className="w-[18px] h-[18px]" />
                  </a>
                )}
                {mapsUrl && (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-blue-200 text-blue-600 active:bg-blue-50"
                    aria-label="Navigate"
                  >
                    <NavigationIcon className="w-[18px] h-[18px]" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Desktop context strip */}
        {!isMobileShell && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 sm:px-4 py-3 mb-3 sm:mb-6">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-x-3 sm:gap-y-1 text-sm text-gray-700 min-w-0">
                <span className="inline-flex items-start gap-1.5 min-w-0">
                  <CalendarIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="min-w-0 break-words">
                    {formatDate(job.scheduled_date || '')}
                    {' · '}
                    {formatTime(job.scheduled_start_time || '')} – {formatTime(job.scheduled_end_time || '')}
                  </span>
                </span>
                <span className="inline-flex items-start gap-1.5 min-w-0">
                  <MapPinIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="min-w-0 break-words">
                    {customerLabel}
                    {job.property?.address ? ` · ${job.property.address}` : ''}
                  </span>
                </span>
                <span className="inline-flex items-start gap-1.5 min-w-0">
                  <UserIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="min-w-0 break-words">{assigneeLabel}</span>
                </span>
                {job.dsny_integration && (
                  <span className="inline-flex self-start px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-800 border border-green-200">
                    DSNY
                    {job.dsny_pickup_days?.length ? ` · ${job.dsny_pickup_days.join(', ')}` : ''}
                  </span>
                )}
              </div>
              {id && canAssignJob && (
                <div className="w-full lg:w-auto lg:shrink-0 lg:min-w-[16rem] space-y-1">
                  <JobAssignmentFields
                    jobId={id}
                    crewId={job.assigned_crew_id}
                    employeeId={job.assigned_employee_id}
                    compact
                    onAssigned={() => fetchJob(id)}
                  />
                  {assignmentHistory.filter((r) => !r.unassigned_at).length > 1 && (
                    <p className="text-[10px] text-gray-500 lg:text-right max-w-full lg:max-w-[14rem]">
                      Primary assignee above. Full team:{' '}
                      {formatAssigneeTeamLabel(assignmentHistory.filter((r) => !r.unassigned_at))}.
                      Add more people from Day → Dispatch.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div
          ref={tabsPanelRef}
          className={`${isMobileShell ? mobile.surface : 'bg-white rounded-lg shadow-sm border border-gray-200'} flex flex-col min-h-0`}
        >
          <ViewTabBar
            tabs={(
              [
                { id: 'overview' as const, label: isMobileShell ? 'Details' : 'Overview' },
                { id: 'work' as const, label: 'Work' },
                { id: 'line_items' as const, label: 'Items', full: 'Line Items' },
                { id: 'gallery' as const, label: 'Gallery' },
                { id: 'notes' as const, label: 'Notes' },
                { id: 'chat' as const, label: 'Chat' },
                { id: 'activity' as const, label: 'Activity' },
              ] as const
            ).filter((tab) => !isMobileShell || (tab.id !== 'notes' && tab.id !== 'chat' && tab.id !== 'work'))}
            activeTab={activeTab}
            onChange={(tab) => {
              setGalleryAutoCaptureToken(0)
              setActiveTab(tab)
            }}
          />

          <div className="p-3 sm:p-6">
            {activeTab === 'work' && id && !isMobileShell && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Work to be done</h3>
                  {job.description ? (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{job.description}</p>
                  ) : (
                    <p className="text-sm text-gray-500">No description.</p>
                  )}
                </div>
                <ApartmentJobWork jobId={id} serviceType={job.service_type} />
                <JobTaskChecklist
                  jobId={id}
                  contractId={job.contract_id}
                  serviceType={job.service_type}
                  showOneOffBadges={!!job.visit_group_id || !!job.is_recurring}
                  generalOnly
                />
                <div className="pt-4 border-t border-gray-100">
                  <JobQrRequirementsPicker jobId={id} propertyId={job.property_id} />
                </div>
              </div>
            )}

            {activeTab === 'overview' && (
              <div className="space-y-6">
              {(job as any).completion_exception_reason && (
                <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-semibold mb-1">Completed with exception</p>
                  <p className="whitespace-pre-wrap">{(job as any).completion_exception_reason}</p>
                  {(job as any).completion_exception_at && (
                    <p className="text-xs text-amber-700 mt-2">
                      {new Date((job as any).completion_exception_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              {!isMobileShell && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <ViewStatCard
                    icon={<FileTextIcon className="w-5 h-5 text-orange-600" />}
                    title="Line Items"
                    value={jobLineItems.length}
                  />
                  <ViewStatCard
                    icon={<ClockIcon className="w-5 h-5 text-sky-600" />}
                    title="Hours Logged"
                    value={totalTimeHours.toFixed(1)}
                  />
                  <ViewStatCard
                    icon={<DollarSignIcon className="w-5 h-5 text-blue-600" />}
                    title="Invoices"
                    value={invoiceLinks.length}
                  />
                </div>
              )}

              {id && (
                <ConversationsPanel entityType="job" entityId={id} customerId={job?.customer_id} />
              )}

              {/* Mobile collapsible associated sections */}
              {isMobileShell && (
                <div className="space-y-2.5 mb-4">
                  {/* Notes: read on Overview; compose via Add note → /notes */}
                  <div className="rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="flex items-center justify-between gap-2 px-4 py-3 min-h-[48px] border-b border-gray-100">
                      <span className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        <StickyNoteIcon className="w-4 h-4 text-gray-500" />
                        Notes
                      </span>
                      {id && (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/jobs/details/${id}/notes`, {
                              state: { from: `/jobs/details/${id}` },
                            })
                          }
                          className="text-sm font-semibold text-blue-600 active:text-blue-800"
                        >
                          Add note
                        </button>
                      )}
                    </div>
                    <div className="px-4 py-3 space-y-3">
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                          Scheduled notes
                        </h4>
                        {job.notes ? (
                          <p className="text-sm text-gray-900 whitespace-pre-wrap">{job.notes}</p>
                        ) : (
                          <p className="text-sm text-gray-500">No scheduled notes on this job.</p>
                        )}
                      </div>
                      {id && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                            Recent comments
                          </h4>
                          <JobCommentsThread jobId={id} readOnly compact limit={5} />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-3 min-h-[48px] border-b border-gray-100">
                      <span className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-gray-500" />
                        Schedule
                      </span>
                    </div>
                    <div className="px-4 py-3 space-y-1">
                      <p className="text-sm text-gray-700">{formatDate(job.scheduled_date || '')}</p>
                      <p className="text-sm text-gray-500">
                        {formatTime(job.scheduled_start_time || '')} –{' '}
                        {formatTime(job.scheduled_end_time || '')}
                      </p>
                      {job.carried_from_job_id && (
                        <button
                          type="button"
                          onClick={() => navigate(`/jobs/details/${job.carried_from_job_id}`)}
                          className="mt-1 text-xs text-blue-600 active:underline"
                        >
                          View original partially completed job
                        </button>
                      )}
                      {job.estimated_duration != null && job.estimated_duration > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Est. duration: {job.estimated_duration} min
                        </p>
                      )}
                      {job.actual_start_time && (
                        <p className="text-xs text-gray-500 mt-2">
                          Actual: {new Date(job.actual_start_time).toLocaleString()}
                          {job.actual_end_time &&
                            ` – ${new Date(job.actual_end_time).toLocaleString()}`}
                        </p>
                      )}
                      {job.is_recurring && (
                        <p className="text-xs text-blue-700 mt-2">
                          Recurring {job.recurrence_type}
                          {job.recurrence_days?.length
                            ? ` · days ${job.recurrence_days.join(', ')}`
                            : ''}
                        </p>
                      )}
                      {job.dsny_integration && (
                        <p className="text-xs text-green-700 mt-1">
                          DSNY
                          {job.dsny_pickup_days?.length
                            ? ` · ${job.dsny_pickup_days.join(', ')}`
                            : ''}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleMobileSection('customer')}
                      className="w-full flex items-center justify-between gap-2 px-4 py-3 min-h-[48px] text-left active:bg-gray-50"
                    >
                      <span className="text-sm font-bold text-gray-900">Customer</span>
                      {mobileSections.customer ? (
                        <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                    {mobileSections.customer && (
                      <div className="px-4 pb-4 flex items-start gap-3 border-t border-gray-100 pt-3">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-sky-700 text-sm font-bold flex-shrink-0">
                          {(customerLabel || '?').charAt(0).toUpperCase()}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900">{customerLabel}</p>
                          {job.customer?.email && (
                            <a
                              href={`mailto:${job.customer.email}`}
                              className="text-sm text-blue-600 break-all"
                            >
                              {job.customer.email}
                            </a>
                          )}
                        </div>
                        {telHref && (
                          <a
                            href={telHref}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-blue-200 text-blue-600 flex-shrink-0"
                            aria-label="Call"
                          >
                            <PhoneIcon className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-gray-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleMobileSection('property')}
                      className="w-full flex items-center justify-between gap-2 px-4 py-3 min-h-[48px] text-left active:bg-gray-50"
                    >
                      <span className="text-sm font-bold text-gray-900">Property</span>
                      {mobileSections.property ? (
                        <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                    {mobileSections.property && (
                      <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
                        <div className="flex items-start gap-3">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 flex-shrink-0">
                            <BuildingIcon className="w-5 h-5" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900">
                              {job.property?.name || 'Property'}
                            </p>
                            <p className="text-sm text-gray-500 mt-0.5 break-words">
                              {propertyAddress || 'No address on file'}
                            </p>
                          </div>
                        </div>
                        {job.property_id && (
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `${propertyDetailPath(job.property_id, fieldHome)}?returnTo=${encodeURIComponent(
                                  `/jobs/details/${job.id}`
                                )}`
                              )
                            }
                            className="w-full min-h-[44px] px-3 py-2 text-sm font-semibold text-blue-700 bg-blue-50 rounded-xl active:bg-blue-100"
                          >
                            View property details
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-gray-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleMobileSection('assigned')}
                      className="w-full flex items-center justify-between gap-2 px-4 py-3 min-h-[48px] text-left active:bg-gray-50"
                    >
                      <span className="text-sm font-bold text-gray-900">Assigned to</span>
                      {mobileSections.assigned ? (
                        <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                    {mobileSections.assigned && (
                      <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
                        <div className="flex items-start gap-3">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 flex-shrink-0">
                            <UserIcon className="w-5 h-5" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900">{assigneeLabel}</p>
                            {job.service_type && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                {getServiceTypeLabel(job.service_type)}
                              </p>
                            )}
                          </div>
                        </div>
                        {id && canAssignJob && (
                          <JobAssignmentFields
                            jobId={id}
                            crewId={job.assigned_crew_id}
                            employeeId={job.assigned_employee_id}
                            compact
                            onAssigned={() => fetchJob(id)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {!isMobileShell && (
                <ViewSection title="Schedule" icon={<CalendarIcon className="w-6 h-6" />} tone="sky">
                  <p className="text-sm text-gray-700">{formatDate(job.scheduled_date || '')}</p>
                  <p className="text-sm text-gray-500">
                    {formatTime(job.scheduled_start_time || '')} – {formatTime(job.scheduled_end_time || '')}
                  </p>
                  {job.carried_from_job_id && (
                    <button
                      type="button"
                      onClick={() => navigate(`/jobs/details/${job.carried_from_job_id}`)}
                      className="mt-2 text-xs text-blue-600 hover:underline"
                    >
                      View original partially completed job
                    </button>
                  )}
                  {job.estimated_duration != null && job.estimated_duration > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Est. duration: {job.estimated_duration} min
                    </p>
                  )}
                  {job.actual_start_time && (
                    <p className="text-xs text-gray-500 mt-2">
                      Actual: {new Date(job.actual_start_time).toLocaleString()}
                      {job.actual_end_time && ` – ${new Date(job.actual_end_time).toLocaleString()}`}
                    </p>
                  )}
                  {job.is_recurring && (
                    <p className="text-xs text-blue-700 mt-2">
                      Recurring {job.recurrence_type}
                      {job.recurrence_days?.length ? ` · days ${job.recurrence_days.join(', ')}` : ''}
                    </p>
                  )}
                  {job.dsny_integration && (
                    <p className="text-xs text-green-700 mt-1">
                      DSNY
                      {job.dsny_pickup_days?.length
                        ? ` · ${job.dsny_pickup_days.join(', ')}`
                        : ''}
                    </p>
                  )}
                </ViewSection>
                )}

                {!isMobileShell && (
                  <ViewSection title="Customer & Property" icon={<MapPinIcon className="w-6 h-6" />} tone="teal">
                    {!fieldHome && (
                      <p className="text-sm text-gray-900">
                        {job.customer?.company_name ||
                          `${job.customer?.contact_first_name || ''} ${job.customer?.contact_last_name || ''}`.trim()}
                      </p>
                    )}
                    <p className="text-sm text-gray-600">{job.property?.name}</p>
                    <p className="text-xs text-gray-500">{job.property?.address}</p>
                    {job.property_id && (
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `${propertyDetailPath(job.property_id, fieldHome)}?returnTo=${encodeURIComponent(
                              `/jobs/details/${job.id}`
                            )}`
                          )
                        }
                        className="mt-2 text-sm text-blue-600 hover:underline font-medium"
                      >
                        View property details →
                      </button>
                    )}
                  </ViewSection>
                )}

                {assignmentHistory.length > 0 && (
                  <ViewSection title="Assignment history" icon={<UserIcon className="w-6 h-6" />} tone="emerald" className="md:col-span-2">
                    <ul className="space-y-2">
                      {assignmentHistory.map((row) => {
                        const who =
                          row.crew?.name ||
                          (row.employee
                            ? `${row.employee.first_name} ${row.employee.last_name}`.trim()
                            : '—')
                        const from = row.assigned_at
                          ? new Date(row.assigned_at).toLocaleString()
                          : '—'
                        const to = row.unassigned_at
                          ? new Date(row.unassigned_at).toLocaleString()
                          : 'present'
                        return (
                          <li
                            key={row.id}
                            className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-700 border-b border-gray-100 pb-2 last:border-0"
                          >
                            <span className="font-medium">{who}</span>
                            <span className="text-xs text-gray-500">
                              {from} → {to}
                              {!row.unassigned_at && (
                                <span className="ml-2 text-green-700 font-medium">active</span>
                              )}
                            </span>
                          </li>
                        )
                      })}
                    </ul>
                    </ViewSection>
                )}

                {canSeePricing && (
                <ViewSection title="Financial" icon={<DollarSignIcon className="w-6 h-6" />} tone="emerald">
                  <p className="text-sm text-gray-700">
                    Billing:{' '}
                    <span className="font-medium capitalize">
                      {(job.billing_type || 'direct').replace('_', ' ')}
                    </span>
                  </p>
                  {(job as any).billing_contract?.contract_number && (
                    <p className="text-sm text-gray-700">
                      Billed via contract {(job as any).billing_contract.contract_number}
                    </p>
                  )}
                  <p className="text-sm text-gray-700">
                    Quoted: {job.quoted_amount ? `$${job.quoted_amount.toLocaleString()}` : '—'}
                  </p>
                  {job.actual_amount != null && (
                    <p className="text-sm text-gray-700">
                      Actual: ${Number(job.actual_amount).toLocaleString()}
                    </p>
                  )}
                  {job.hours_actual != null && Number(job.hours_actual) > 0 && (
                    <p className="text-sm text-gray-700">
                      Hours actual: {Number(job.hours_actual).toFixed(2)}
                    </p>
                  )}
                  {job.materials_cost != null && Number(job.materials_cost) > 0 && (
                    <p className="text-sm text-gray-700">
                      Materials: ${Number(job.materials_cost).toLocaleString()}
                    </p>
                  )}
                  {invoiceLinks.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">{invoiceLinks.length} linked invoice(s)</p>
                  )}
                </ViewSection>
                )}

                <ViewSection title="Timelog" icon={<ClockIcon className="w-6 h-6" />} tone="blue">
                  <p className="text-sm text-gray-700">{timeEntries.length} entries</p>
                  <p className="text-sm text-gray-500">{totalTimeHours.toFixed(2)} hours logged</p>
                </ViewSection>

                <ViewSection title="Related" icon={<FileTextIcon className="w-6 h-6" />} tone="orange" className="md:col-span-2">
                  {job.contract_id && job.contract && (
                    <button
                      onClick={() => navigate(`/contracts/details/${job.contract_id}`)}
                      className="text-sm text-blue-600 hover:underline block"
                    >
                      Contract {job.contract.contract_number}
                    </button>
                  )}
                  {job.proposal_id && job.proposal && (
                    <button
                      onClick={() => navigate(`/proposals/view/${job.proposal_id}`)}
                      className="text-sm text-blue-600 hover:underline block"
                    >
                      Proposal {job.proposal.proposal_number}
                    </button>
                  )}
                  {linkedCityRecord && (
                    <div className="mt-2 text-sm text-gray-700">
                      <p className="font-medium text-gray-900">
                        City record · {linkedCityRecord.source || 'Unknown'}
                      </p>
                      <p>{linkedCityRecord.title || linkedCityRecord.source_uid || 'Linked record'}</p>
                      {linkedCityRecord.category && (
                        <p className="text-xs text-gray-500">{linkedCityRecord.category}</p>
                      )}
                    </div>
                  )}
                  {siblingJobs.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{siblingJobs.length} related job(s)</p>
                  )}
                  {!job.contract_id &&
                    !job.proposal_id &&
                    !linkedCityRecord &&
                    siblingJobs.length === 0 && (
                    <p className="text-sm text-gray-500">No related records.</p>
                  )}
                </ViewSection>
              </div>
              <div className="mt-4">
                <JobFieldCapturePanel job={job} />
              </div>
              </div>
            )}

            {activeTab === 'line_items' && (
              <div className="space-y-6">
                {jobLineItems.length === 0 ? (
                  <ViewSection title="Line Items" icon={<FileTextIcon className="w-6 h-6" />} tone="orange">
                    <ViewEmptyState
                      tone="orange"
                      icon={<FileTextIcon className="w-10 h-10 text-orange-300" />}
                      message="No line items on this job."
                    />
                  </ViewSection>
                ) : (
                  <ViewSection title="Line Items" icon={<FileTextIcon className="w-6 h-6" />} tone="orange">
                    {/* Mobile: stacked cards */}
                    <ul className="space-y-2 md:hidden">
                      {jobLineItems.map((item) => (
                        <li
                          key={item.id}
                          className="rounded-lg border border-gray-200 p-3 space-y-1"
                        >
                          <p className="text-sm font-medium text-gray-900 break-words">
                            {item.description}
                          </p>
                          <p className="text-xs text-gray-600">
                            Qty:{' '}
                            {item.is_variable ? 'Variable' : item.quantity}
                            {item.unit_type ? ` ${formatUnitTypeLabel(item.unit_type)}` : ''}
                          </p>
                          {canSeePricing && (
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600 pt-1">
                              <span>Rate ${Number(item.unit_price).toFixed(2)}</span>
                              <span className="font-medium text-gray-900">
                                Total ${Number(item.total_price || 0).toFixed(2)}
                              </span>
                              <span className="capitalize">{item.billing_status || 'pending'}</span>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                    {/* Desktop: table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-500">Description</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-500">Qty</th>
                            {canSeePricing && (
                              <>
                                <th className="px-4 py-2 text-left font-medium text-gray-500">Rate</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-500">Total</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-500">Billing</th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {jobLineItems.map((item) => (
                            <tr key={item.id}>
                              <td className="px-4 py-2">{item.description}</td>
                              <td className="px-4 py-2">
                                {item.is_variable ? 'Variable' : item.quantity}{' '}
                                {item.unit_type ? formatUnitTypeLabel(item.unit_type) : ''}
                              </td>
                              {canSeePricing && (
                                <>
                                  <td className="px-4 py-2">${Number(item.unit_price).toFixed(2)}</td>
                                  <td className="px-4 py-2">${Number(item.total_price || 0).toFixed(2)}</td>
                                  <td className="px-4 py-2 capitalize">{item.billing_status || 'pending'}</td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  {canSeePricing && invoiceLinks.length > 0 && (
                    <div className="mt-4 p-3 sm:p-4 bg-white rounded-lg border border-orange-100">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Invoices</h4>
                      <ul className="space-y-1">
                        {invoiceLinks.map((inv) => (
                          <li key={inv.invoice_id} className="text-sm text-gray-700">
                            {inv.invoice_number} · {inv.status} · ${inv.balance_due.toFixed(2)} due
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {siblingJobs.length > 0 && (
                    <div className="mt-4 p-3 sm:p-4 bg-white rounded-lg border border-orange-100">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Related jobs</h4>
                      <ul className="space-y-1">
                        {siblingJobs.map((s) => (
                          <li key={s.id}>
                            <button
                              type="button"
                              onClick={() => navigate(`/jobs/details/${s.id}`)}
                              className="min-h-[44px] text-left text-sm text-blue-600 hover:underline py-2"
                            >
                              {s.job_number} · {formatDate(s.scheduled_date || '')} ·{' '}
                              {formatStatusLabel(s.status)}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  </ViewSection>
                )}
              </div>
            )}

            {activeTab === 'notes' && id && !isMobileShell && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Job notes</h3>
                  {job.notes ? (
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{job.notes}</p>
                  ) : (
                    <p className="text-sm text-gray-500">No scheduled notes on this job.</p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Add a note</h3>
                  <JobCommentsThread jobId={id} />
                </div>
              </div>
            )}

            {activeTab === 'chat' && id && !isMobileShell && (
              <div className="space-y-8">
                {canJobChat ? (
                  <JobChatPanel jobId={id} />
                ) : (
                  <p className="text-sm text-gray-500">You do not have permission to use job chat.</p>
                )}
                <PatchInPanel jobId={id} customerId={job.customer_id} />
              </div>
            )}

            {activeTab === 'gallery' && id && (
              <JobGallery jobId={id} autoCaptureToken={galleryAutoCaptureToken} />
            )}

            {activeTab === 'activity' && (
              <ViewSection title="Activity" icon={<FileTextIcon className="w-6 h-6" />} tone="indigo">
                {activities.length === 0 ? (
                  <ViewEmptyState tone="indigo" message="No activity recorded yet." />
                ) : (
                  <div className="space-y-3">
                    {activities.map((activity) => (
                      <div key={activity.id} className="flex items-start gap-3 py-3 border-b border-indigo-100 last:border-0">
                        <div className="p-2 bg-white rounded-lg">
                          <FileTextIcon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">{activity.description}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatActivityMeta(activity)} · {activity.activity_type.replace(/_/g, ' ')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ViewSection>
            )}
          </div>
        </div>

        <JobCompletionModal
          jobId={id!}
          isOpen={showCompletionModal}
          autoHours={fieldHome}
          onClose={() => {
            setShowCompletionModal(false)
            setPendingStatus(null)
          }}
          onSaved={async () => {
            if (pendingStatus) await applyStatusChange(pendingStatus)
            setPendingStatus(null)
          }}
        />

        <CompletionExceptionModal
          open={showExceptionModal}
          gateMessage={exceptionGateMessage}
          busy={updatingStatus}
          onCancel={() => {
            setShowExceptionModal(false)
            setPendingStatus(null)
            setExceptionGateMessage(null)
          }}
          onConfirm={async (reason) => {
            if (!pendingStatus) return
            setShowExceptionModal(false)
            await applyStatusChange(pendingStatus, { reason })
            setPendingStatus(null)
            setExceptionGateMessage(null)
          }}
        />
      </div>
      </div>

      {/* Sticky field action bar — sits above PWA bottom nav */}
      {showMobileActionBar && (
        <div className={mobile.stickyCta} style={{ bottom: actionBarBottom }}>
          <div className="max-w-6xl mx-auto space-y-2">
            {primaryActions.map((nextStatus) => {
              const isChecklistCta = nextStatus === 'completed' && !checklistComplete
              return (
                <button
                  key={nextStatus}
                  type="button"
                  onClick={() => {
                    if (isChecklistCta && id) {
                      navigate(`/jobs/details/${id}/checklist`, {
                        state: { from: `/jobs/details/${id}`, photoMode: true },
                      })
                      return
                    }
                    runStatusAction(nextStatus)
                  }}
                  disabled={updatingStatus}
                  className={
                    nextStatus === 'completed' && !isChecklistCta
                      ? mobile.stickyCtaBtnSuccess
                      : mobile.stickyCtaBtn
                  }
                >
                  {updatingStatus ? (
                    <Loader2Icon className="w-5 h-5 animate-spin" />
                  ) : isChecklistCta ? (
                    <ClipboardListIcon className="w-5 h-5" />
                  ) : nextStatus === 'completed' ? (
                    <CheckCircleIcon className="w-5 h-5" />
                  ) : (
                    <PlayIcon className="w-5 h-5" />
                  )}
                  {isChecklistCta
                    ? 'Checklist'
                    : statusActionLabel(nextStatus, { mobile: true })}
                </button>
              )
            })}
            {secondaryActions.length > 0 && (
              <div className="flex gap-2">
                {secondaryActions.map((nextStatus) => (
                  <button
                    key={nextStatus}
                    type="button"
                    onClick={() => runStatusAction(nextStatus)}
                    disabled={updatingStatus}
                    className={`flex-1 min-h-[44px] px-3 py-2 text-sm font-medium rounded-xl border transition-colors disabled:opacity-50 ${
                      nextStatus === 'cancelled'
                        ? 'border-red-300 text-red-600 active:bg-red-50'
                        : 'border-gray-300 text-gray-700 active:bg-gray-50'
                    }`}
                  >
                    {statusActionLabel(nextStatus)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
